import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { SectionRenderer } from '../editor/SectionRenderer';
import { FloatingCtaBar } from '../common/FloatingCtaBar';
import { PlatformAttribution } from '../common/PlatformAttribution';
import { WebsiteThemeProvider } from '../../theme/WebsiteThemeProvider';
import { YachtItem, BlogPost, YachtReservation, PublishedSiteSnapshot } from '../../types';
import { PublicYachtDetailModal } from '../yachts/PublicYachtDetailModal';
import { realtimeDbService as dbService } from '../../services/realtimeDbService';
import { checkSubmissionRateLimit, escapeHtml, sanitizeUrl, getSiteDisplayAddress } from '../../utils/security';
import confetti from 'canvas-confetti';
import { getSiteThemeVariables } from '../../utils/contrast';
import { LivePreviewHeader } from './LivePreviewHeader';
import {
  X,
  Globe,
  ExternalLink,
  ShieldCheck,
  RotateCw,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Users,
  Anchor,
  CheckCircle2,
  Send,
  Sparkles,
  AlertTriangle,
  Layers,
} from 'lucide-react';

interface LiveSiteRendererProps {
  onClose: () => void;
}

export const LiveSiteRenderer: React.FC<LiveSiteRendererProps> = ({ onClose }) => {
  const { activeSite, refreshSites } = useSite();
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [selectedYacht, setSelectedYacht] = useState<YachtItem | null>(null);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [targetYachtForReservation, setTargetYachtForReservation] = useState<YachtItem | undefined>(undefined);
  const [selectedBlogPost, setSelectedBlogPost] = useState<BlogPost | null>(null);
  const [publishedSnapshot, setPublishedSnapshot] = useState<PublishedSiteSnapshot | null>(null);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState<boolean>(true);

  // Reservation form state
  const [resName, setResName] = useState('');
  const [resEmail, setResEmail] = useState('');
  const [resPhone, setResPhone] = useState('');
  const [resStart, setResStart] = useState('2026-06-15');
  const [resEnd, setResEnd] = useState('2026-06-22');
  const [resGuests, setResGuests] = useState(6);
  const [resNotes, setResNotes] = useState('');
  const [resSubmitted, setResSubmitted] = useState(false);
  const [honeypotValue, setHoneypotValue] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeSite) return;
    let isMounted = true;
    setIsLoadingSnapshot(true);
    // Real aggregate view count tracking
    dbService.incrementSiteViewCount(activeSite.id).catch((err) => console.warn('View count increment error:', err));
    dbService.getPublishedSnapshot(activeSite.id).then((snapshot) => {
      if (isMounted) {
        setPublishedSnapshot(snapshot);
        setIsLoadingSnapshot(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [activeSite?.id]);

  if (!activeSite) return null;

  // Use publishedPages from snapshot if available; fall back to draft pages if not yet published
  const pagesToRender = (publishedSnapshot?.pages && publishedSnapshot.pages.length > 0
    ? publishedSnapshot.pages
    : (activeSite.publishedPages && activeSite.publishedPages.length > 0
        ? activeSite.publishedPages
        : (activeSite.pages || []))) || [];

  const currentPage =
    pagesToRender.find((p) => p && p.slug === currentPath) ||
    pagesToRender.find((p) => p && p.isHomePage) ||
    pagesToRender[0];

  const domain = getSiteDisplayAddress(activeSite);

  const handleOpenReservation = (yacht?: YachtItem) => {
    setTargetYachtForReservation(yacht);
    setIsReservationModalOpen(true);
    setResSubmitted(false);
    setFormError(null);
  };

  const handleReservationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // 1. Anti-bot honeypot check
    if (honeypotValue.trim() !== '') {
      console.warn('Bot detected via honeypot.');
      setResSubmitted(true);
      return;
    }

    // 2. Rate limit check (max 5 per 60s)
    const isAllowed = checkSubmissionRateLimit(`reservation_${activeSite.id}`, 5, 60000);
    if (!isAllowed) {
      setFormError('Çok sık talep gönderdiniz. Lütfen birkaç saniye bekleyin.');
      return;
    }

    if (!resName.trim() || !resPhone.trim()) {
      setFormError('Lütfen ad soyad ve telefon numaranızı girin.');
      return;
    }

    const newReservation: Partial<YachtReservation> = {
      id: `res_${Date.now().toString(36)}`,
      siteId: activeSite.id,
      yachtId: targetYachtForReservation?.id || 'general',
      yachtName: escapeHtml(targetYachtForReservation?.name || 'Genel Mavi Tur Talebi'),
      customerName: escapeHtml(resName.trim()),
      customerEmail: escapeHtml(resEmail.trim() || 'misafir@example.com'),
      customerPhone: escapeHtml(resPhone.trim()),
      startDate: resStart,
      endDate: resEnd,
      guestCount: Number(resGuests),
      notes: escapeHtml(resNotes.trim()),
      status: 'PENDING',
      createdAt: Date.now(),
      totalEstimatedPrice: targetYachtForReservation ? targetYachtForReservation.pricePerDay * 7 : 15000,
      currency: targetYachtForReservation?.currency || 'EUR',
    };

    try {
      await dbService.saveReservation(activeSite.id, newReservation);
      await refreshSites();

      setResSubmitted(true);
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
      });
    } catch (err: any) {
      setFormError('Talep iletilirken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex flex-col animate-in fade-in select-text">
      {/* Top Browser Simulator Bar (Rule 10 & Rule 14 Compliant LivePreviewHeader) */}
      <LivePreviewHeader
        site={activeSite}
        currentPath={currentPath}
        publishedSnapshot={publishedSnapshot}
        onClose={onClose}
        onRefresh={() => {
          if (refreshSites) refreshSites();
        }}
      />

      {/* Main Live Site Content Area */}
      <WebsiteThemeProvider
        siteSettings={activeSite?.settings}
        websiteId={activeSite?.id}
        className="flex-1 overflow-y-auto flex flex-col"
      >
        {/* Navigation Bar inside the rendered site (only if no dedicated HEADER or NAVIGATION section exists) */}
        {!Boolean(
          currentPage?.sections?.some(
            (s) =>
              s &&
              (s.type === 'HEADER' ||
                s.type?.toUpperCase() === 'HEADER' ||
                (s as any).category === 'NAVIGATION' ||
                s.name?.toLowerCase().includes('navigasyon') ||
                s.name?.toLowerCase().includes('header') ||
                s.id?.toLowerCase().includes('header')) &&
              s.isVisible !== false
          ) ||
            pagesToRender?.some((p) =>
              p.sections?.some(
                (s) =>
                  s &&
                  (s.type === 'HEADER' || s.type?.toUpperCase() === 'HEADER') &&
                  s.isVisible !== false
              )
            )
        ) && (
          <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100 px-6 py-4 flex items-center justify-between shadow-xs">
            <div
              className="text-lg font-black text-slate-900 tracking-tight cursor-pointer"
              onClick={() => setCurrentPath('/')}
            >
              {activeSite.name}
            </div>
            <div className="flex items-center gap-6">
              {pagesToRender
                .filter((p) => p.isPublished)
                .map((page) => (
                  <button
                    key={page.id}
                    onClick={() => setCurrentPath(page.slug)}
                    className={`text-xs font-bold transition tracking-wide ${
                      currentPath === page.slug
                        ? 'text-blue-600 border-b-2 border-blue-600 pb-0.5'
                        : 'text-slate-600 hover:text-slate-950'
                    }`}
                  >
                    {page.title}
                  </button>
                ))}
              <button
                onClick={() => handleOpenReservation()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-500/20 transition"
              >
                {activeSite.industry === 'YACHT_RENTAL' ? 'Rezervasyon Talebi' : 'İletişim & Teklif'}
              </button>
            </div>
          </nav>
        )}

        {/* Render sections of current page */}
        {currentPage && currentPage.sections && currentPage.sections.length > 0 ? (
          <div className="flex-1">
            {currentPage.sections
              .filter((sec) => sec.isVisible)
              .map((section) => (
                <SectionRenderer
                  key={section.id}
                  section={section}
                  settings={activeSite?.settings}
                  industry={activeSite.industry}
                  siteData={activeSite}
                  activePage={currentPage}
                  onNavigate={(slug) => {
                    if (slug.startsWith('/#') || slug.startsWith('#')) {
                      const anchor = slug.replace(/^\/?#/, '');
                      const el = document.getElementById(anchor) || document.querySelector(`[data-section-id="${anchor}"]`) || document.querySelector(`#${anchor}`);
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth' });
                      }
                      return;
                    }
                    setCurrentPath(slug);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  onSelectYacht={(yacht) => setSelectedYacht(yacht)}
                  onOpenReservationModal={(yacht) => handleOpenReservation(yacht)}
                  onOpenBlogPost={(post) => setSelectedBlogPost(post)}
                />
              ))}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-slate-500">
            <Globe className="w-12 h-12 text-slate-300 mb-3 animate-pulse" />
            <h3 className="text-lg font-bold text-slate-800 mb-1">Bu Sayfada Bölüm Bulunmuyor</h3>
            <p className="text-xs text-slate-400 max-w-sm">
              Editor panelinden bu sayfaya başlık, servisler veya galeri gibi yeni bölümler ekleyebilirsiniz.
            </p>
          </div>
        )}

        {/* Mandatory Platform Attribution (Adiosoft Website Builder ile yapılmıştır) */}
        <PlatformAttribution
          bgColor={
            currentPage?.sections?.find((s) => s.type === 'FOOTER' && s.isVisible)?.style?.backgroundColor ||
            currentPage?.sections?.filter((s) => s.isVisible).slice(-1)[0]?.style?.backgroundColor ||
            activeSite?.settings?.accentColor ? undefined : '#020617'
          }
        />
      </WebsiteThemeProvider>



      {/* ========================================================================= */}
      {/* RESERVATION REQUEST MODAL */}
      {/* ========================================================================= */}
      {isReservationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col">
            <div className="px-6 py-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center justify-between">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <Anchor className="w-4 h-4 text-cyan-400" />
                  {targetYachtForReservation
                    ? `${targetYachtForReservation.name} — Rezervasyon Talebi`
                    : 'Mavi Tur Rezervasyon Talebi'}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  Talebiniz doğrudan şirket yetkilisine iletilecektir.
                </p>
              </div>
              <button
                onClick={() => setIsReservationModalOpen(false)}
                className="p-1 rounded-lg hover:bg-slate-700 text-slate-300 transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {resSubmitted ? (
              <div className="p-8 text-center space-y-3">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-bold text-slate-900">Talebiniz Alındı!</h4>
                <p className="text-xs text-slate-500 max-w-xs mx-auto">
                  Rezervasyon talebiniz başarıyla kaydedildi. Temsilcimiz en kısa sürede sizinle iletişime geçecektir.
                </p>
                <div className="pt-3">
                  <button
                    onClick={() => setIsReservationModalOpen(false)}
                    className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleReservationSubmit} className="p-6 space-y-3.5 text-xs text-slate-700">
                {formError && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs flex items-center gap-2 font-medium">
                    <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0" />
                    {formError}
                  </div>
                )}

                {/* Honeypot field (hidden from real users) */}
                <input
                  type="text"
                  name="bot_field"
                  value={honeypotValue}
                  onChange={(e) => setHoneypotValue(e.target.value)}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div>
                  <label className="block font-semibold mb-1">Adınız & Soyadınız *</label>
                  <input
                    type="text"
                    required
                    placeholder="Örn: Mehmet Yılmaz"
                    value={resName}
                    onChange={(e) => setResName(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900 placeholder:text-slate-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold mb-1">Telefon Numarası *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+90 (5XX) XXX XX XX"
                      value={resPhone}
                      onChange={(e) => setResPhone(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900 placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">E-posta Adresi</label>
                    <input
                      type="email"
                      placeholder="adiniz@example.com"
                      value={resEmail}
                      onChange={(e) => setResEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900 placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block font-semibold mb-1">Başlangıç</label>
                    <input
                      type="date"
                      value={resStart}
                      onChange={(e) => setResStart(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Bitiş</label>
                    <input
                      type="date"
                      value={resEnd}
                      onChange={(e) => setResEnd(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Kişi Sayısı</label>
                    <input
                      type="number"
                      min="1"
                      max="30"
                      value={resGuests}
                      onChange={(e) => setResGuests(Number(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold mb-1">Özel İstekler / Rota Tercihi</label>
                  <textarea
                    rows={2}
                    placeholder="Göcek koyları, Bodrum, aşçı tercihi vb..."
                    value={resNotes}
                    onChange={(e) => setResNotes(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-medium text-slate-900 placeholder:text-slate-400"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold rounded-xl shadow-lg transition flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Rezervasyon Talebini Gönder
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* BLOG POST DETAIL MODAL */}
      {/* ========================================================================= */}
      {selectedBlogPost && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="relative h-56 bg-slate-100 overflow-hidden shrink-0">
              <img
                src={selectedBlogPost.coverImage}
                alt={selectedBlogPost.title}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setSelectedBlogPost(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-slate-900/70 text-white hover:bg-slate-900 transition"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-4 left-4 px-3 py-1 rounded-lg bg-blue-600 text-white font-bold text-xs">
                {selectedBlogPost.category}
              </div>
            </div>

            <div className="p-6 overflow-y-auto space-y-4 text-xs text-slate-700">
              <div>
                <h2 className="text-xl font-extrabold text-slate-900">{selectedBlogPost.title}</h2>
                <div className="flex items-center gap-4 text-slate-400 mt-2 text-[11px] pb-3 border-b border-slate-100">
                  <span>Yazar: {selectedBlogPost.authorName}</span>
                  <span>{selectedBlogPost.readTimeMinutes} dk okuma</span>
                </div>
              </div>

              <div className="text-slate-600 leading-relaxed text-sm whitespace-pre-line">
                {selectedBlogPost.content || selectedBlogPost.summary}
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end">
                <button
                  onClick={() => setSelectedBlogPost(null)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl font-bold"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ========================================================================= */}
      {/* YACHT DETAIL MODAL */}
      {/* ========================================================================= */}
      {selectedYacht && (
        <PublicYachtDetailModal
          yacht={selectedYacht}
          onClose={() => setSelectedYacht(null)}
          onBookYacht={(yacht) => {
            setSelectedYacht(null);
            handleOpenReservation(yacht);
          }}
          primaryColor={activeSite?.settings?.primaryColor || '#0284C7'}
        />
      )}

      {/* Mobile Sticky Floating CTA Bar */}
      <FloatingCtaBar
        siteData={activeSite}
        onOpenReservation={() => handleOpenReservation(undefined)}
      />
    </div>
  );
};

export default LiveSiteRenderer;
